const BASE_URL = 'https://api.github.com'
const DEFAULT_OPTIONS = {
    accept: 'application/vnd.github.v3+json', // from github api
    type: 'all',
    sort: 'updated',
    direction: 'asc',
    per_page: 50,
    page: 0,
}
const cache = new Map()
const cacheExpiration = 60 * 60 // seconds until cache expiration 1 hour
/**
 * @typedef {Object} APIOptions
 * Check out GitHub API for more info {@link https://docs.github.com/en/free-pro-team@latest/rest/reference/repos#list-repositories-for-a-user}
 * @property {string} accept the file to fetch the datasource from
 * @property {string} username the user to fetch the data from
 * @property {string} type Can be one of all, owner, member
 * @property {string} sort Can be one of created, updated, pushed, full_name.
 * @property {string} direction Can be one of asc or desc. Default: asc when using full_name, otherwise desc
 * @property {number} per_page Results per page (max 100)
 * @property {number} page Page number of the results to fetch.
 */

/**
* Get the data from the supplied route
* @param {APIOptions} options
* @returns {Object} JSON of Repos from GITHUB
*/
export async function getUserRepos(options) {
    // get all the api options from the options object combined
    // merged with the default options and options (overwriting the default options)
    // in a new empty object 
    const {
        accept,
        username,
        type,
        sort,
        direction,
        per_page,
        page
    } = Object.assign({}, DEFAULT_OPTIONS, options)

    // get the entry that is stored in the cache under the key of the username
    // check if the entry is truthy and if its not expired using the cache entry date time
    const cacheEntry = cache.get(username)
    if (cacheEntry && !isExpired(cacheEntry?.time)) {
        return cacheEntry.data
    }

    // if we do not have a cached item fetch the info and set it into the cache
    const data = await fetchUserRepos({ accept, username, type, sort, direction, per_page, page })
    cache.set(username, { data, time: Date.now() })
    console.log(data)
    return data
}

/**
* Function to get the user repo statistics, for now just lanugage info
* @param {object} repos the array holding the users repos
* @returns {Map} Map of the lanauges and their occurences in the users repos
*/
export function getUserRepoStatistics(repos) {
    if (!repos) return null

    // create a map that will hold the languages and their totals
    const languageMap = new Map()
    // loop through the repos langages, using object destructuring
    repos.forEach(({ language }) => {
        const oldValue = (languageMap.has(language)) ? languageMap.get(language) : 0
        languageMap.set(language, oldValue + 1)
    })
    // languageMap.set('total', repos.length)
    return languageMap
}

/**
* Helper function to check cache expiry
* @param {number} cacheTime UNIX timestamp in seconds
*/
const isExpired = (cacheTime) => {
    const currentTimeInSeconds = Math.round(Date.now() / 1000)
    const timePassed = currentTimeInSeconds - cacheTime
    return timePassed > (cacheExpiration || 300) // default to 5 minutes if no expiration is set
}


async function fetchUserRepos({ accept, username, type, sort, direction, per_page, page }) {
    // create the URI from the base url and the username and the query string after
    const url = `${BASE_URL}/users/${username}/repos?type=${type}&sort=${sort}&direction=${direction}&per_page=${per_page}&page=${page}`
    // create headers to define that we will be accepting json data
    let headerParams = new Headers()
    headerParams.append('Accept', `${accept}`)

    //crate a new request with the url, headers and the GET method as we will only be reading data
    let request = new Request(url, {
        headers: headerParams,
        method: 'GET'
    })

    // preform the fetch if the response is not ok, throw an error with the response text
    const response = await fetch(request)
    if (!response.ok) throw new Error(response.statusText)

    return response.json()
}

const exportFunctions = { getUserRepos, getUserRepoStatistics }
export default exportFunctions
